import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FaqItem } from "../../models/faq";
import FaqService from "../../services/api/faq.service";
import {
  ActionBtn,
  Btn,
  Card,
  DragHandle,
  Input,
  Textarea,
} from "../../style/admin/FaqAdmin_style";
import QuillEditor from "../../components/QuillEditor";
import { nanoid } from "nanoid";
import { toast } from "../../utils/notify";
import { ToneItem } from "../../models/tone";
import ToneService from "../../services/api/tone.service";
import { DocumentNoteHeader } from "../../AdminLayout_Style";
import { BlogListWrapper } from "../../style/admin/Blog_Style";
import TiptapEditor from "../../components/TipTapEditor";
import { envConfig } from "../../config/env.config";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/* ---------- Item draggable ---------- */
function SortableFaqItem({
  item,
  index,
  updateFaq,
  cloneFaq,
  removeFaq,
  faqs,
}: any) {
  const isValid =
    item.question?.trim() && item.answer && item.answer !== "<p><br></p>";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.clientId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card dragging={isDragging}>
        <DragHandle
          {...(isValid ? attributes : {})}
          {...(isValid ? listeners : {})}
          style={{
            opacity: isValid ? 1 : 0.4,
            cursor: isValid ? "grab" : "not-allowed",
          }}>
          ☰
        </DragHandle>

        <ActionBtn onClick={() => cloneFaq(index)}>+</ActionBtn>
        <ActionBtn danger onClick={() => removeFaq(index)}>
          ✕
        </ActionBtn>

        <Input
          placeholder={`Tiêu đề #${index + 1}`}
          value={item.question}
          onChange={(e) => updateFaq(index, "question", e.target.value)}
        />

        <TiptapEditor
          value={item.answer}
          onChange={(value: string) => updateFaq(index, "answer", value)}
          height={200}></TiptapEditor>

        {/* <QuillEditor
          height={200}
          value={item.answer}
          onChange={(value: string) => updateFaq(index, "answer", value)}
        /> */}
      </Card>
    </div>
  );
}

interface FaqRef {
  submit: () => Promise<void>;
  validate: () => Promise<boolean>;
}

/* ---------- Main ---------- */
const ToneAdmin = forwardRef<FaqRef>(() => {
  const [tones, setTones] = useState<ToneItem[]>([]);

  type Language = typeof envConfig.KEY_BASE extends "vn"
    ? "VI" | "EN"
    : "VI" | "EN" | "ID" | "TH";

  const [language, setLanguage] = useState<Language>(
    envConfig.KEY_BASE === "vn" ? "VI" : "EN",
  );

  const isFaqEmpty = (faq: ToneItem) =>
    faq.question.trim() === "" && isEmptyQuill(faq.answer);

  const loadData = async () => {
    // ✅ filter theo tab language

    const res = await ToneService.getAll(language);
    if (res.success) {
      const filtered = (res.data || []).filter(
        (x: any) => x.language === language,
      );

      if (!filtered?.length) {
        setTones([createEmptyFaq()]);
        return;
      }

      setTones(
        filtered.map((x: any) => ({
          ...x,
          clientId: nanoid(), // 👈 QUAN TRỌNG (thiếu cái này drag sẽ lỗi + state lệch)
        })),
      );
    }
  };

  useEffect(() => {
    loadData();
  }, [language]);

  const sensors = useSensors(useSensor(PointerSensor));

  const cloneFaq = (index: number) => {
    const newFaq = createEmptyFaq();

    setTones((prev) => {
      const next = [...prev];

      // chèn vào vị trí index + 1
      next.splice(index + 1, 0, newFaq);

      return next;
    });
  };
  const updateFaq = (
    index: number,
    field: "question" | "answer",
    value: string,
  ) => {
    const clone = [...tones];
    clone[index][field] = value;
    setTones(clone);
  };

  const onDragEnd = (event: any) => {
    if (hasInvalidFaq(tones)) {
      alert("Vui lòng nhập đầy đủ trước khi sắp xếp");
      return;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setTones((prev) => {
      const oldIndex = prev.findIndex((x) => x.clientId === active.id);
      const newIndex = prev.findIndex((x) => x.clientId === over.id);

      const reordered = arrayMove(prev, oldIndex, newIndex);

      // ✅ cập nhật position luôn
      return reordered.map((x, i) => ({
        ...x,
        position: i + 1,
      }));
    });
  };

  const persistFaqs = async (list: ToneItem[]) => {
    const res = await ToneService.createOrUpdate({
      translationTones: list.map((x) => ({
        id: x.id,
        question: x.question,
        answer: x.answer,
        position: x.position,
        language,
      })),
      language,
    });

    if (res.success) {
      toast("Cập nhật thành công", "success");
    } else {
      toast("Cập nhật thất bại", "error");
    }
  };

  const createEmptyFaq = (): ToneItem => ({
    id: EMPTY_GUID,
    clientId: nanoid(), // 👈 thêm dòng này
    question: "",
    answer: "",
    position: 0,
  });

  const removeFaq = (index: number) => {
    setTones((prev) => {
      if (prev.length === 1) {
        return [createEmptyFaq()]; // ✅ sạch 100%
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const isEmptyQuill = (html: string) =>
    !html || html === "<p><br></p>" || html.trim() === "";

  const isFaqValid = (faq: ToneItem) =>
    faq.question.trim() !== "" && !isEmptyQuill(faq.answer);

  const hasInvalidFaq = (list: ToneItem[]) => {
    // ✅ chỉ có 1 FAQ & nó đang hoàn toàn rỗng → OK
    if (list.length === 1 && isFaqEmpty(list[0])) {
      return false;
    }

    // ❌ các trường hợp còn lại → check nghiêm
    return list.some((faq) => !isFaqValid(faq));
  };

  return (
    <>
      <DocumentNoteHeader>
        <h3>Danh sách giọng điệu</h3>
        <button
          onClick={() => persistFaqs(tones)}
          style={{
            background: "linear-gradient(135deg, #4f46e5, #6366f1)",
            color: "white",
            padding: "10px 18px",
            border: "none",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.25s ease",
            boxShadow: "0 4px 10px rgba(79, 70, 229, 0.3)",
          }}>
          Lưu
        </button>
      </DocumentNoteHeader>

      <BlogListWrapper>
        <div>
          {envConfig.KEY_BASE === "vi" ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <Btn
                  type="button"
                  onClick={() => setLanguage("VI")}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    opacity: language === "VI" ? 1 : 0.6,
                  }}>
                  🇻🇳 Tiếng Việt
                </Btn>

                <Btn
                  type="button"
                  onClick={() => setLanguage("EN")}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    opacity: language === "EN" ? 1 : 0.6,
                    marginLeft: 8,
                  }}>
                  🇬🇧 English
                </Btn>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <Btn
                  type="button"
                  onClick={() => setLanguage("EN")}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    opacity: language === "EN" ? 1 : 0.6,
                  }}>
                  🇬🇧 English
                </Btn>

                <Btn
                  type="button"
                  onClick={() => setLanguage("ID")}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    opacity: language === "ID" ? 1 : 0.6,
                    marginLeft: 8,
                  }}>
                  🇮🇩 Indonesia
                </Btn>

                <Btn
                  type="button"
                  onClick={() => setLanguage("TH")}
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    opacity: language === "TH" ? 1 : 0.6,
                    marginLeft: 8,
                  }}>
                  🇹🇭 Thai
                </Btn>
              </div>
            </>
          )}

          {tones.length > 0 && (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={hasInvalidFaq(tones) ? undefined : onDragEnd}>
                <SortableContext
                  items={tones.map((x) => x.clientId)}
                  strategy={verticalListSortingStrategy}>
                  {tones.map((item, index) => (
                    <SortableFaqItem
                      key={item.clientId}
                      item={item}
                      index={index}
                      updateFaq={updateFaq}
                      cloneFaq={cloneFaq}
                      removeFaq={removeFaq}
                      faqs={tones}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </BlogListWrapper>
    </>
  );
});

export default ToneAdmin;
